/**
 * Movimento do LeadExplorer (design system de movimento, 23/10/2026): o estado por trás das
 * trocas no lugar, dos números que rolam e das janelas que saem com transição. Só comportamento:
 * as classes (.ds-troca, .ds-rola, .ds-dialogo…) ficam em styles/movimento.css e os componentes
 * que as usam em TrocaNoLugar.tsx.
 *
 * Fora do navegador (testes) os tokens valem 0: o que sai some na hora.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { usePresenca } from '../../hooks/usePresenca';
import { tokenMs } from '../../lib/movimento';

/**
 * Janela que o pai monta a partir de um valor (as análises a apagar, a baixar…): continua montada
 * com o último valor enquanto sai e diz quando pôr `.is-aberto`.
 */
export function useSaida<T>(
  valor: T | null,
  saida: string = '--duracao-140',
): { atual: T | null; aberto: boolean } {
  const [ultimo, setUltimo] = useState<T | null>(valor);
  if (valor !== null && valor !== ultimo) setUltimo(valor);
  const { montado, aberto } = usePresenca(valor !== null, saida);
  return { atual: montado ? (valor ?? ultimo) : null, aberto };
}

/**
 * Foco nunca se perde: enquanto `ativo`, guarda quem tinha o foco ao abrir (o gatilho); ao fechar
 * (ou desmontar), devolve o foco a ele na hora, sem esperar a saída. Se a pessoa já foi para outro
 * lugar da página, fica onde está. O gatilho é lido antes da pintura (useLayoutEffect); a volta
 * fica para depois do commit (useEffect), senão o React devolve o foco à peça que ainda está
 * saindo.
 */
export function useDevolverFoco(ativo: boolean): void {
  const gatilho = useRef<HTMLElement | null>(null);
  useLayoutEffect(() => {
    if (!ativo) return;
    const antes = document.activeElement;
    gatilho.current = antes instanceof HTMLElement && antes !== document.body ? antes : null;
  }, [ativo]);
  useEffect(() => {
    if (!ativo) return undefined;
    return () => {
      const alvo = gatilho.current;
      if (!alvo?.isConnected) return;
      const agora = document.activeElement;
      const naJanela = agora?.closest(
        '[role="dialog"], [role="alertdialog"], [role="presentation"]',
      );
      if (agora && agora !== document.body && !naJanela) return;
      alvo.focus({ preventScroll: true });
    };
  }, [ativo]);
}

/** Chama `quando` dois quadros depois (o primeiro pinta o estado de partida da transição). */
function depoisDeUmQuadro(quando: () => void): () => void {
  if (typeof window.requestAnimationFrame !== 'function') {
    quando();
    return () => undefined;
  }
  let feito = false;
  const uma = (): void => {
    if (feito) return;
    feito = true;
    quando();
  };
  let segundo = 0;
  const primeiro = window.requestAnimationFrame(() => {
    segundo = window.requestAnimationFrame(uma);
  });
  // Reserva para a aba em segundo plano, onde o navegador para os quadros (usePresenca faz igual).
  const reserva = window.setTimeout(uma, 60);
  return () => {
    window.cancelAnimationFrame(primeiro);
    if (segundo) window.cancelAnimationFrame(segundo);
    window.clearTimeout(reserva);
  };
}

/**
 * Troca no lugar (.ds-troca): quando a chave muda, o que estava na tela fica como `saindo` até a
 * saída terminar e o novo nasce com `.ds-fora` por um quadro (`entrou` falso), para a transição
 * partir do apagado. Trocar de novo no meio volta de onde estava; sem fila.
 */
export function useTrocaNoLugar<T>(
  chave: string,
  valor: T,
  saida: string = '--duracao-140',
): { saindo: { chave: string; valor: T } | null; entrou: boolean } {
  const ultimo = useRef({ chave, valor });
  const [chaveVista, setChaveVista] = useState(chave);
  const [saindo, setSaindo] = useState<{ chave: string; valor: T } | null>(null);
  const [entrou, setEntrou] = useState(true);

  if (chave !== chaveVista) {
    setChaveVista(chave);
    setSaindo(ultimo.current.chave === chave ? null : ultimo.current);
    setEntrou(false);
  }

  useLayoutEffect(() => {
    ultimo.current = { chave, valor };
  });

  useEffect(() => {
    if (entrou) return undefined;
    return depoisDeUmQuadro(() => setEntrou(true));
  }, [entrou]);

  useEffect(() => {
    if (!saindo) return undefined;
    const ms = tokenMs(saida);
    if (ms <= 0) {
      setSaindo(null);
      return undefined;
    }
    const timer = window.setTimeout(() => setSaindo(null), ms);
    return () => window.clearTimeout(timer);
  }, [saindo, saida]);

  return { saindo, entrou };
}

/** No máximo 4 rolagens por segundo (uma a cada 250 ms), como no NumeroQueRola de places. */
const INTERVALO_MINIMO_MS = 250;

/** O número que está saindo (enquanto rola), o sentido e uma chave que muda a cada troca. */
interface Rolagem {
  saindo: number | null;
  desce: boolean;
  chave: number;
}

/**
 * Número que rola (.ds-rola): o valor anterior sai para cima (ou para baixo, quando o número
 * desce) enquanto o novo entra, em --duracao-200. Trocou menos de 250 ms depois da troca
 * anterior (digitando rápido)? O número salta para o atual, sem rolar: no máximo 4 rolagens por
 * segundo. A conta fica num efeito de layout (antes de pintar), não no render, para valer igual
 * no StrictMode.
 */
export function useNumeroQueRola(valor: number, duracao: string = '--duracao-200'): Rolagem {
  const [rolagem, setRolagem] = useState<Rolagem>({ saindo: null, desce: false, chave: 0 });
  const anterior = useRef(valor);
  const ultimaTroca = useRef(Number.NEGATIVE_INFINITY);

  useLayoutEffect(() => {
    const antes = anterior.current;
    if (antes === valor) return;
    anterior.current = valor;
    const agora = performance.now();
    const rola = agora - ultimaTroca.current >= INTERVALO_MINIMO_MS && tokenMs(duracao) > 0;
    ultimaTroca.current = agora;
    setRolagem((r) => ({
      saindo: rola ? antes : null,
      desce: valor < antes,
      chave: r.chave + 1,
    }));
  }, [valor, duracao]);

  // O que saiu deixa o DOM quando a rolagem termina.
  useEffect(() => {
    if (rolagem.saindo === null) return undefined;
    const timer = window.setTimeout(
      () => setRolagem((r) => ({ ...r, saindo: null })),
      tokenMs(duracao),
    );
    return () => window.clearTimeout(timer);
  }, [rolagem.saindo, rolagem.chave, duracao]);

  return rolagem;
}

/**
 * Separa um texto pronto (já traduzido) em antes e depois de um trecho: o número que rola no meio
 * de "Faltam 4 números…". Sem o trecho, devolve null e o texto fica inteiro, sem rolar.
 */
export function partirEm(texto: string, trecho: string): [string, string] | null {
  const i = trecho ? texto.indexOf(trecho) : -1;
  if (i < 0) return null;
  return [texto.slice(0, i), texto.slice(i + trecho.length)];
}

/** Espera um token de duração (setTimeout). Com o token em 0 (testes), não espera. */
export function esperar(nome: string): Promise<void> | null {
  const ms = tokenMs(nome);
  if (ms <= 0) return null;
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

/**
 * Posição de um elemento dentro de um ancestral posicionado, pelos offsets (não pelos retângulos
 * da tela): a janela que ainda está entrando, com escala, não entorta a conta.
 */
export function posicaoEm(el: HTMLElement, ancestral: HTMLElement): { x: number; y: number } {
  let x = 0;
  let y = 0;
  let atual: HTMLElement | null = el;
  while (atual && atual !== ancestral) {
    x += atual.offsetLeft;
    y += atual.offsetTop;
    atual = atual.offsetParent as HTMLElement | null;
  }
  return { x, y };
}
